import { Component, OnInit } from '@angular/core';

import { ProfileHistoryGroup, User, UserService, UsersStore } from '@fry/lib/users';
import { ActivatedRoute } from '@angular/router';
import { UserFieldsStore } from '@fry/lib/user-fields';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { FormBuilderService, FormData, FormProviderService } from '@fry/lib/forms';
import { concatMap, filter, map, reduce, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '@fry/lib/auth';
import { NotifyService } from '@fry/lib/utils';
import { SecurityService } from '@fry/lib/security';
import { MatDialog } from '@angular/material/dialog';
import { FormDialogComponent } from '@fry/components/common/form';

import { UserLanguageService } from '../user-language/user-language.service';
import { FileUploadControlComponentDelegate } from '@fry/components/common/file-upload-control';
import { UserFileUploadDelegate } from '../user-edit/user-edit-details/user-file-upload.service';

interface LinkAction {
  type: 'link';
  label: string;
  link: Observable<any>;
  condition: () => Observable<boolean>;
}

interface ButtonAction {
  type: 'button';
  label: string;
  click: () => any;
  condition: () => Observable<boolean>;
}

type Button = LinkAction | ButtonAction;

@Component({
    selector: 'app-user-view',
    templateUrl: './user-view.component.html',
    styleUrls: ['./user-view.component.scss'],
    providers: [
        UserService,
        { provide: FileUploadControlComponentDelegate, useClass: UserFileUploadDelegate },
    ],
    standalone: false
})
export class UserViewComponent implements OnInit {
  public fullname: string;
  public user: User;
  public formData$: Observable<FormData>;
  public editUrl$: Observable<string | null>;
  private _buttons$ = new BehaviorSubject<any>([]);
  public buttons$ = this._buttons$.pipe(
    switchMap(data => this.processButtons(data))
  );
  public disabledReason: string;
  public roles: string[];
  public history: ProfileHistoryGroup[];

  constructor(
    private userLanguageService: UserLanguageService,
    private users: UsersStore,
    private userService: UserService,
    private route: ActivatedRoute,
    private userFields: UserFieldsStore,
    private builder: FormBuilderService,
    private provider: FormProviderService,
    private auth: AuthService,
    private notify: NotifyService,
    private security: SecurityService,
    private dialog: MatDialog,
    private fileUploadDelegate: FileUploadControlComponentDelegate,
  ) {}

  ngOnInit() {
    this.route.data.subscribe(data => {
      this.userService.setUser(data.user);
      this.fileUploadDelegate.setContext({ oid: data.user.id });
      this.init();
    });
  }

  init() {
    this.setupUser();
    this.formData$ = this.userFields.defaultForm().pipe(
      switchMap(form => this.builder.buildForm(form, { readOnly: true })),
      switchMap(fields => {
          return this.provider.getFormDataWith(fields, this.user.userFields)
        }
      )
    );

    // TODO: Add permission check
    // this.editUrl$ = this.auth.isUserCurrent(this.user.doc.username).pipe(
    //   map(isCurrent => {
    //     const id = isCurrent ? 'profile' : this.user.doc.username;
    //     return `/users/${id}/edit/basic`;
    //   })
    // );

    this.setupButtons();
  }

  private setupUser() {
    this.user = this.userService.user;
    if (this.user.doc.state === 'disabled') {
      const lastAction = this.user.getLastAction('user_disable');
      if (lastAction) {
        this.disabledReason = lastAction.reason;
      }
    }
    this.roles = this.user.getRoles();
    this.history = this.user.history();
  }

  private setupButtons() {
    const _buttons: Button[] = [
      {
        type: 'link',
        label: $localize `Account security`,
        link: this.auth.isUserCurrent(this.user.doc.username).pipe(
          map(isCurrent => {
            const id = isCurrent ? 'profile' : this.user.doc.username;
            return `/users/${id}/credentials`;
          })
        ),
        condition: () => {
           return of(true);
        }
      },
      {
        type: 'link',
        label: $localize `Edit profile`,
        link: this.auth.isUserCurrent(this.user.doc.username).pipe(
          map(isCurrent => {
            const id = isCurrent ? 'profile' : this.user.doc.username;
            return `/users/${id}/edit`;
          })
        ),
        condition: () => {
          return this.security.hasOwnPermissionFor(
            'users.edit',
            this.user.doc.username
          );
        }
      },
      {
        type: 'button',
        label: $localize `Disable this user`,
        click: this.disable.bind(this),
        condition: () => {
          if (this.user.doc.state !== 'active') {
            return of(false);
          }

          return this.security.hasPermissionFor(
            'users.review',
            this.user.doc.username
          );
        }
      },
      {
        type: 'button',
        label: $localize `Enable this user`,
        click: this.enable.bind(this),
        condition: () => {
          if (this.user.doc.state !== 'disabled') {
            return of(false);
          }

          return this.security.hasPermissionFor(
            'users.review',
            this.user.doc.username
          );
        }
      }
    ];
    this._buttons$.next(_buttons);
  }

  private processButtons(buttons: Button[]): Observable<Button[]> {
    return from(buttons).pipe(
      concatMap(btn => {
        return btn.condition().pipe(
          filter(bool => bool),
          map(() => btn)
        );
      }),
      reduce((acc: Button[], btn: Button) => [...acc, btn], [])
    );
  }

  disable() {
    const res = {};
    const fields = [
      {
        id: 'reason',
        title:
          $localize `Please provide a reason for disabling this account.
          The user will see this reason when they next attempt to login to this account.`,
        initial: '',
        widget: { type: 'text' },
        validators: []
      }
    ];
    this.provider.getFormDataWith(fields, res).subscribe(form => {
      this.dialog.open(FormDialogComponent, {
        data: {
          title: $localize `Disable user`,
          form,
          onSubmit: value => {
            return this.users.disable(this.user, value).pipe(
              tap(user => {
                this.userService.user = user;
                this.setupUser();
                this.notify.success($localize `User has been disabled`);
                this.setupButtons();
              })
              // catchError((error) => {
              // this.notify.error(error);
              // return of();
              // })
            );
          }
        }
      });
    });
  }

  enable() {
    this.users.enable(this.user).subscribe(
      user => {
        this.userService.user = user;
        this.setupUser();
        this.notify.success($localize `User has been enabled`);
        this.setupButtons();
      },
      error => {
        this.notify.error(error);
      }
    );
  }

  canUserSetLanguage(): boolean {
    return this.userLanguageService.availableLanguages.length > 1;
  }
}
